import { useState, useCallback } from 'react';

interface FormData {
  nombre: string;
  descripcion: string;
  color: string;
}

export interface UseAddTagModalReturn {
  formData: FormData;
  loading: boolean;
  error: string | null;
  handleFormChange: (field: string, value: string) => void;
  handleSave: (onSave: (data: FormData) => Promise<void>) => Promise<void>;
  handleClose: (onClose: () => void) => void;
  resetForm: () => void;
}

export const useAddTagModal = (): UseAddTagModalReturn => {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    descripcion: '',
    color: '#9CA3AF', // Default gray color
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({ nombre: '', descripcion: '', color: '#9CA3AF' });
    setError(null);
  }, []);

  const handleSave = useCallback(
    async (onSave: (data: FormData) => Promise<void>) => {
      // Validation
      if (!formData.nombre.trim()) {
        setError('El nombre es requerido');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await onSave(formData);

        // Reset form on success
        resetForm();
      } catch (err: any) {
        setError(err?.body?.message || err?.message || 'Error al crear la etiqueta');
      } finally {
        setLoading(false);
      }
    },
    [formData, resetForm],
  );

  const handleClose = useCallback(
    (onClose: () => void) => {
      resetForm();
      onClose();
    },
    [resetForm],
  );

  return {
    formData,
    loading,
    error,
    handleFormChange,
    handleSave,
    handleClose,
    resetForm,
  };
};
